import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { weeklyDateNavigationDriverFactory } from '../WeeklyDateNavigation/WeeklyDateNavigation.driver';

export const weeklyDatePickerDriverFactory = (base: UniDriver) => {
  const weeklyDateNavigationDriver = weeklyDateNavigationDriverFactory(base);
  const getDayOfDate = (year, month, day) =>
    base.$(`button[data-date='${year}-${month}-${day}']:not([aria-disabled])`);
  const getSelectedDay = () =>
    base.$(`.DayPicker-Day[aria-selected="true"]>div>span[aria-hidden="true"]`);
  const getSelectedDayButton = () =>
    base.$(`.DayPicker-Day[aria-selected="true"]`);
  const getWeekdaysText = () =>
    base
      .getAll('.DayPicker-Weekday')
      .then(async (elements) =>
        elements.map(async (element) => element.text()),
      );
  const getDatesText = () =>
    base
      .getAll('.DayPicker-Day')
      .then(async (elements) =>
        elements.map(async (element) => element.text()),
      );
  const getTodayDate = async () => {
    const today = await base.$(
      '.DayPicker-Day--today > div > span[aria-hidden="true"]',
    );
    return today.text();
  };
  const getFocusedDay = () => base.$('.DayPicker-Day:focus');
  const getCaptionElement = () => base.$('[data-hook$="caption"]');
  const getElementByTabIndex = (tabIndex: number) =>
    base.$$(
      `.DayPicker-Day[tabindex="${tabIndex}"] > div > span[aria-hidden="true"]`,
    );

  return {
    ...baseUniDriverFactory(base),
    /**
     * Click on a given day
     * @param date - a date to select
     */
    clickDay: async (date) => {
      const day = getDayOfDate(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      await day.click();
    },
    /**
     * Click on the prev week button
     */
    clickOnPrevWeekButton: async () =>
      weeklyDateNavigationDriver.clickOnPrevWeekButton(),
    /**
     * Click on the next week button
     */
    clickOnNextWeekButton: async () =>
      weeklyDateNavigationDriver.clickOnNextWeekButton(),
    /**
     * Get the header caption text
     */
    getCaptionText: async () => weeklyDateNavigationDriver.getCaptionText(),

    /**
     * Get selected day
     */
    getSelectedDate: async () => getSelectedDay().text(),

    /**
     * Get the tabIndex of the selected day button
     */
    getSelectedDayTabIndex: async () => getSelectedDayButton().attr('tabindex'),

    /**
     * Get the next week arrow button
     */
    getNextWeekArrowButton: async () => base.$('[data-hook="next-arrow"]'),

    /**
     * Return the list of weekday names
     */
    getWeekdaysText: async () => getWeekdaysText(),

    /**
     * Return the list of dates
     */
    getDatesText: async () => getDatesText(),

    /**
     * Get the today date
     */
    getTodayDate,

    /**
     * Get element by selector
     */
    $: async (selector: string) => base.$(selector),

    pressLeftArrowKey: () => getFocusedDay().pressKey('ArrowLeft'),
    /**
     * Simulating pressing on the right arrow key on the keyboard
     */
    pressRightArrowKey: () => getFocusedDay().pressKey('ArrowRight'),
    /**
     * Return the focused day text content
     */
    getDatesByTabIndex: async (tabIndex) =>
      getElementByTabIndex(tabIndex).map(async (element) => element.text()),
    /**
     * Return the data-date attribute of the currently focused day.
     */
    getFocusedDate: async () => {
      const focused = await getFocusedDay();
      return focused.attr('data-date');
    },
    /**
     * Return the caption element
     */
    getCaptionElement,
  };
};

export type WeeklyDatePickerDriver = ReturnType<
  typeof weeklyDatePickerDriverFactory
>;
